import React, { FC, useCallback, useState } from 'react';
import { graphql, useMutation, useRelayEnvironment } from 'react-relay/hooks';
import { commitMutation, fetchQuery } from 'relay-runtime';
import { salesChannelsLabels } from 'src/components/pages/products/utils';
import { Input } from 'src/components/widgets/input';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { T, toLocalID } from 'src/shared/helpers';
import { useAccountData } from 'src/shared/hooks/useAccountData';
import { useSubscriptionData } from 'src/shared/hooks/useSubscriptionData';
import { useValidationContext, validateRequiredTrimmed, ValidationStateProvider } from 'src/shared/validation';
import styled from 'styled-components';
import { Dialog, DialogWindow, useDialogManager } from '../../../widgets/dialog';
import { FormControl } from '../../../widgets/formControl';
import { NotificationBanner } from '../../../widgets/notificationBanner';
import { showErrorNotification, showSuccessNotification } from '../../../widgets/notificationToaster';
import { Radio } from '../../../widgets/radio';
import { SubSection } from '../../../widgets/section';
import { AdvancedSelect } from '../../../widgets/select';
import Stack from '../../../widgets/stack/stack';
import { createStandaloneSiteDialogCheckDomainQuery } from './__generated__/createStandaloneSiteDialogCheckDomainQuery.graphql';
import { createStandaloneSiteDialogForkThemeMutation } from './__generated__/createStandaloneSiteDialogForkThemeMutation.graphql';
import { createStandaloneSiteDialogMarketplaceQuery } from './__generated__/createStandaloneSiteDialogMarketplaceQuery.graphql';
import { createStandaloneSiteDialogMutation } from './__generated__/createStandaloneSiteDialogMutation.graphql';
import { getStandaloneSiteSubdomain, getStandaloneSiteSubdomainValidationMessage, validateStandaloneSiteSubdomain } from './standaloneSiteNavigation';

const StyledDialogWindow = styled(DialogWindow)`
    max-width: 600px;

    > div:nth-child(3) {
        overflow-y: visible;
    }
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid ${(props) => props.theme.app.borderColor};
    margin: 4px 0;
`;

interface SalesChannel {
    readonly id: string;
    readonly detail: {
        readonly id: string;
        readonly properties: {
            readonly name: string | null;
        } | null;
    } | null;
    readonly ecommerceSettings: {
        readonly id: string;
        readonly ecommerceName: string;
        readonly definitiveUrl: string | null;
        readonly remoteUrl: string | null;
    } | null;
}

interface CreateStandaloneSiteDialogProps {
    salesChannels: readonly SalesChannel[];
    onCreated: (site: { id: string; standaloneSiteID: number }) => void;
}

const NAME_VALIDATION_KEY = 'createStandaloneSiteName';
const DOMAIN_VALIDATION_KEY = 'createStandaloneSiteDomain';
const SALES_CHANNEL_VALIDATION_KEY = 'createStandaloneSiteSalesChannel';

const checkDomainQuery = graphql`
    query createStandaloneSiteDialogCheckDomainQuery($domain: String!) {
        checkStandaloneSiteDomain(domain: $domain) {
            isAvailable
            message
        }
    }
`;

const marketplaceThemesQuery = graphql`
    query createStandaloneSiteDialogMarketplaceQuery {
        marketplaceThemes {
            marketplaceThemeID
            name
        }
    }
`;

const forkThemeMutation = graphql`
    mutation createStandaloneSiteDialogForkThemeMutation($input: StandaloneSiteForkMarketplaceThemeInput!) {
        forkMarketplaceTheme(input: $input) {
            id
            standaloneSiteThemeID
            status
        }
    }
`;

const CreateStandaloneSiteDialogContent: FC<CreateStandaloneSiteDialogProps> = ({ salesChannels, onCreated }) => {
    const { closeDialog } = useDialogManager();
    const environment = useRelayEnvironment();
    const validationContext = useValidationContext();
    const { userInfo } = useAccountData();
    const { subscription } = useSubscriptionData();
    const [name, setName] = useState('');
    const [domain, setDomain] = useState('');
    const [productSource, setProductSource] = useState<'scratch' | 'channel'>('scratch');
    const [selectedSalesChannelId, setSelectedSalesChannelId] = useState<string | null>(salesChannels.length === 1 ? salesChannels[0].id : null);
    const [isHeadlessCart, setIsHeadlessCart] = useState(false);
    const [shopifyHeadlessStorefrontToken, setShopifyHeadlessStorefrontToken] = useState('');
    const [domainError, setDomainError] = useState<string | null>(null);
    const [isCheckingDomain, setIsCheckingDomain] = useState(false);
    const hasStandalonePlanFeature = subscription?.plan?.additionalFeatures.standalone === true;
    const hasStandalonePlanOverride = useFeatureFlagWithConditions('standaloneSitePlanOverride', {
        userId: userInfo?.userID?.toString()
    });
    const isStandaloneAvailable = hasStandalonePlanFeature || hasStandalonePlanOverride;
    const standalonePlanDisabledTooltip = !isStandaloneAvailable
        ? T._('This feature is disabled because your current plan does not include Mini-Sites.', 'Admin')
        : undefined;

    const [createSite, isCreating] = useMutation<createStandaloneSiteDialogMutation>(graphql`
        mutation createStandaloneSiteDialogMutation($input: StandaloneSiteCreateInput!) {
            createStandaloneSite(input: $input) {
                id
                standaloneSiteID
                name
                domain
                status
            }
        }
    `);

    const scratchChannel = salesChannels.find((sc) => sc.ecommerceSettings?.ecommerceName === 'NONE');
    const linkedChannels = salesChannels.filter((sc) => sc.ecommerceSettings?.ecommerceName !== 'NONE');

    const resolvedChannel = salesChannels.find((sc) => sc.id === (productSource === 'scratch' ? scratchChannel?.id : selectedSalesChannelId));
    const isShopifyChannel = resolvedChannel?.ecommerceSettings?.ecommerceName === 'SHOPIFY';

    const salesChannelOptions = linkedChannels.map((sc) => {
        const name = sc.detail?.properties?.name ? sc.detail.properties.name : salesChannelsLabels(sc.ecommerceSettings?.ecommerceName ?? '', true);
        const url = sc.ecommerceSettings?.definitiveUrl || sc.ecommerceSettings?.remoteUrl;
        return {
            value: sc.id,
            label: url ? `${name} (${url})` : name
        };
    });

    const handleDomainChange = useCallback(
        (value: string) => {
            setDomain(value);
            setDomainError(null);
            validationContext.update(DOMAIN_VALIDATION_KEY, undefined);
        },
        [validationContext]
    );

    const resolvedChannelId = productSource === 'scratch' ? scratchChannel?.id : selectedSalesChannelId;

    const handleCreate = async () => {
        if (!isStandaloneAvailable || !validationContext.submit()) return;

        const normalizedDomain = getStandaloneSiteSubdomain(domain);

        if (productSource === 'scratch' && !scratchChannel?.id)
            showErrorNotification({ message: T._('No from-scratch channel available. Please contact support.', 'Admin') });

        if (!normalizedDomain || (productSource === 'scratch' && !scratchChannel?.id)) return;

        const salesChannelLocalId = toLocalID(resolvedChannelId!);
        if (salesChannelLocalId == null) {
            showErrorNotification({ message: T._('Please select a valid online store.', 'Admin') });
            return;
        }

        // Check domain availability before creating
        setIsCheckingDomain(true);
        try {
            const result = await fetchQuery<createStandaloneSiteDialogCheckDomainQuery>(environment, checkDomainQuery, {
                domain: normalizedDomain
            }).toPromise();

            if (!result?.checkStandaloneSiteDomain?.isAvailable) {
                const message = result?.checkStandaloneSiteDomain?.message || T._('This domain is not available.', 'Admin');
                setDomainError(message);
                validationContext.update(DOMAIN_VALIDATION_KEY, message);
                validationContext.submit();
                setIsCheckingDomain(false);
                return;
            }
        } catch {
            showErrorNotification({ message: T._('Failed to validate domain.', 'Admin') });
            setIsCheckingDomain(false);
            return;
        }
        setIsCheckingDomain(false);

        createSite({
            variables: {
                input: {
                    name: name.trim(),
                    domain: normalizedDomain,
                    salesChannelID: salesChannelLocalId,
                    shopifyHeadlessStorefrontToken: isHeadlessCart && isShopifyChannel ? shopifyHeadlessStorefrontToken.trim() || null : null
                }
            },
            onCompleted: (response) => {
                showSuccessNotification({ message: T._('Mini-Site site created.', 'Admin') });

                const created = response.createStandaloneSite;

                // Assign default marketplace theme
                const siteId = created?.id;
                if (siteId) {
                    fetchQuery<createStandaloneSiteDialogMarketplaceQuery>(environment, marketplaceThemesQuery, {})
                        .toPromise()
                        .then((result) => {
                            const firstTheme = result?.marketplaceThemes?.[0];
                            if (firstTheme) {
                                commitMutation<createStandaloneSiteDialogForkThemeMutation>(environment, {
                                    mutation: forkThemeMutation,
                                    variables: {
                                        input: {
                                            standaloneSiteId: siteId,
                                            marketplaceThemeID: firstTheme.marketplaceThemeID
                                        }
                                    }
                                });
                            }
                        });
                }

                if (created) {
                    onCreated({ id: created.id, standaloneSiteID: created.standaloneSiteID });
                }
            },
            onError: () => {
                showErrorNotification({ message: T._('Failed to create Mini-Site site.', 'Admin') });
            },
            updater: (store) => store.invalidateStore()
        });
    };

    return (
        <Dialog
            windowDecorator={StyledDialogWindow}
            title={T._('Create Mini-Site', 'Admin')}
            buttons={[
                {
                    label: isCheckingDomain ? T._('Checking domain...', 'Admin') : T._('Create', 'Admin'),
                    onClick: handleCreate,
                    disabled: !isStandaloneAvailable || isCreating || isCheckingDomain,
                    tooltip: standalonePlanDisabledTooltip
                }
            ]}
            onClose={() => closeDialog('create-standalone-site')}
        >
            <Stack $gap='16px'>
                {!isStandaloneAvailable && (
                    <NotificationBanner
                        type='EXTRA'
                        content={
                            <>
                                {T._("Mini-Sites are not included in your current plan. If you're interested in this feature, contact ", 'Admin')}
                                <a href='mailto:help@zakeke.com'>help@zakeke.com</a>
                                {T._('.', 'Admin')}
                            </>
                        }
                    />
                )}
                <FormControl label={T._('Name', 'Admin')} caption={T._('A display name to identify this portal in your dashboard.', 'Admin')}>
                    <Input
                        value={name}
                        onChange={(e) => {
                            setName(e.currentTarget.value);
                        }}
                        placeholder={T._('Enter site name', 'Admin')}
                        validationKey={NAME_VALIDATION_KEY}
                        validationLabel={T._('Name', 'Admin')}
                        validate={validateRequiredTrimmed}
                        validationMessage={T._('Name is required.', 'Admin')}
                        disabled={!isStandaloneAvailable}
                    />
                </FormControl>

                <FormControl label={T._('Domain', 'Admin')} caption={T._('Your site will be available at your-domain.customhub.app', 'Admin')}>
                    <Input
                        value={domain}
                        onChange={(e) => handleDomainChange(e.currentTarget.value)}
                        placeholder={T._('Enter subdomain', 'Admin')}
                        validationKey={DOMAIN_VALIDATION_KEY}
                        validationLabel={T._('Domain', 'Admin')}
                        validate={(value) => !!domainError || validateStandaloneSiteSubdomain(value)}
                        validationMessage={(value) => domainError || getStandaloneSiteSubdomainValidationMessage(value)}
                        disabled={!isStandaloneAvailable}
                    />
                </FormControl>

                <Divider />

                <SubSection title={T._('Product source', 'Admin')} contentGap='8px'>
                    <Radio
                        id='source-scratch'
                        name='productSource'
                        label={T._('From scratch', 'Admin')}
                        caption={T._('Use products created directly in Zakeke, not linked to any e-commerce.', 'Admin')}
                        value='scratch'
                        checked={productSource === 'scratch'}
                        onSelect={() => setProductSource('scratch')}
                        disabled={!isStandaloneAvailable}
                    />
                    <Radio
                        id='source-channel'
                        name='productSource'
                        label={T._('From an online store', 'Admin')}
                        caption={T._('Use products imported from an existing online store (e.g. Shopify).', 'Admin')}
                        value='channel'
                        checked={productSource === 'channel'}
                        onSelect={() => setProductSource('channel')}
                        disabled={!isStandaloneAvailable}
                    />
                    <div style={{ visibility: productSource === 'channel' ? 'visible' : 'hidden' }}>
                        <AdvancedSelect
                            placeholder={T._('Select an online store', 'Admin')}
                            options={salesChannelOptions}
                            value={salesChannelOptions.find((o) => o.value === selectedSalesChannelId) || null}
                            onChange={(option) => {
                                setSelectedSalesChannelId(option ? option.value : null);
                            }}
                            menuPosition={'fixed'}
                            validationKey={SALES_CHANNEL_VALIDATION_KEY}
                            validationLabel={T._('Online store', 'Admin')}
                            validate={(value) => productSource === 'channel' && !value}
                            validationMessage={T._('Online store is required.', 'Admin')}
                            isDisabled={!isStandaloneAvailable}
                        />
                    </div>
                </SubSection>

                {isShopifyChannel && (
                    <SubSection title={T._('Headless Shopify cart', 'Admin')} contentGap='8px'>
                        <Radio
                            id='headless-cart-no'
                            name='headlessCart'
                            label={T._('Standard behavior', 'Admin')}
                            caption={T._('Completed customizations create a Shopify draft order (in-store flow).', 'Admin')}
                            value='no'
                            checked={!isHeadlessCart}
                            onSelect={() => setIsHeadlessCart(false)}
                            disabled={!isStandaloneAvailable}
                        />
                        <Radio
                            id='headless-cart-yes'
                            name='headlessCart'
                            label={T._('Headless cart', 'Admin')}
                            caption={T._('Use the selected Shopify sales channel for the shop domain and provide its Storefront API token below.', 'Admin')}
                            value='yes'
                            checked={isHeadlessCart}
                            onSelect={() => setIsHeadlessCart(true)}
                            disabled={!isStandaloneAvailable}
                        />
                        {isHeadlessCart && (
                            <>
                                <FormControl
                                    label={T._('Storefront API token', 'Admin')}
                                    caption={T._('The public Storefront API access token of the headless sales channel.', 'Admin')}
                                >
                                    <Input
                                        value={shopifyHeadlessStorefrontToken}
                                        onChange={(e) => setShopifyHeadlessStorefrontToken(e.currentTarget.value)}
                                        placeholder={T._('Enter Storefront API token', 'Admin')}
                                        disabled={!isStandaloneAvailable}
                                    />
                                </FormControl>
                            </>
                        )}
                    </SubSection>
                )}
            </Stack>
        </Dialog>
    );
};

const CreateStandaloneSiteDialog: FC<CreateStandaloneSiteDialogProps> = (props) => (
    <ValidationStateProvider>
        <CreateStandaloneSiteDialogContent {...props} />
    </ValidationStateProvider>
);

export default CreateStandaloneSiteDialog;
